/**
 * Weekly adaptive coaching review (T12.6 / ISSUE-072 / ADR-0026).
 *
 * Premium screen: shows expenditure estimate, proposed targets, and the
 * deterministic "why changed" explanation. Free users see a teaser only.
 * CALIBRATING state is shown when the engine lacks sufficient data.
 */
import type { AppEventName, CoachingReviewResponse } from '@nutians/shared/schemas';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CycleInsightsPanel } from '../components/CycleInsightsPanel.js';
import { EnergyAvailabilityNotice } from '../components/EnergyAvailabilityNotice.js';
import { PremiumGate } from '../components/PremiumGate.js';
import {
  useCoachingExpenditure,
  useCoachingHistory,
  useCoachingReview,
} from '../hooks/useCoaching.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import { useTrack } from '../hooks/useTrack.js';

function confidenceLabel(
  confidence: number,
  t: ReturnType<typeof useTranslation<'coach'>>['t'],
): string {
  if (confidence < 0.4) return t('review.expenditure.confidenceLow');
  if (confidence < 0.75) return t('review.expenditure.confidenceMedium');
  return t('review.expenditure.confidenceHigh');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function MacroRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-ink-soft">{label}</span>
      <span className="font-medium text-ink">{value} g</span>
    </div>
  );
}

interface ProposalPanelProps {
  proposal: NonNullable<CoachingReviewResponse['proposal']>;
  customKcal: string;
  onCustomKcalChange: (v: string) => void;
  onAccept: () => void;
  onKeep: () => void;
  accepting: boolean;
  keeping: boolean;
}

function ProposalPanel({
  proposal,
  customKcal,
  onCustomKcalChange,
  onAccept,
  onKeep,
  accepting,
  keeping,
}: ProposalPanelProps) {
  const { t } = useTranslation('coach');

  return (
    <div className="rounded-2xl border border-husk bg-paper px-5 py-5">
      <p className="mb-4 text-sm font-medium text-ink">{t('review.proposal.title')}</p>
      <div className="mb-3 flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-ink">
          {t('review.proposal.targetKcal', { value: proposal.kcal })}
        </span>
        {proposal.cappedBySafety && (
          <span className="text-xs text-ink-soft">{t('review.proposal.safetyNote')}</span>
        )}
      </div>
      <div className="divide-y divide-husk">
        <MacroRow label={t('review.proposal.protein')} value={proposal.proteinG} />
        <MacroRow label={t('review.proposal.carbs')} value={proposal.carbsG} />
        <MacroRow label={t('review.proposal.fat')} value={proposal.fatG} />
        <MacroRow label={t('review.proposal.fiber')} value={proposal.fiberG} />
      </div>
      <p className="mt-3 text-xs text-ink-soft">
        {t('review.proposal.effectiveFrom', { date: formatDate(proposal.effectiveFrom) })}
      </p>
      <div className="mt-4">
        <label htmlFor="custom-kcal" className="block text-xs text-ink-soft">
          {t('review.proposal.editLabel')}
        </label>
        <input
          id="custom-kcal"
          type="number"
          min={1000}
          max={6000}
          step={10}
          value={customKcal}
          onChange={(e) => onCustomKcalChange(e.target.value)}
          placeholder={t('review.proposal.editPlaceholder', { value: proposal.kcal })}
          className="mt-1 w-full rounded-xl border border-husk bg-shell px-3 py-2 text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-leaf"
        />
      </div>
      <div className="mt-5 flex flex-col gap-2">
        <button
          type="button"
          onClick={onAccept}
          disabled={accepting || keeping}
          className="w-full rounded-xl bg-leaf px-5 py-2.5 text-sm font-semibold text-white hover:bg-leaf-deep disabled:opacity-60"
        >
          {accepting ? t('review.accepting') : t('review.accept')}
        </button>
        <button
          type="button"
          onClick={onKeep}
          disabled={accepting || keeping}
          className="w-full rounded-xl border border-husk bg-transparent px-5 py-2.5 text-sm font-medium text-ink-soft hover:text-ink disabled:opacity-60"
        >
          {keeping ? t('review.keeping') : t('review.keep')}
        </button>
      </div>
    </div>
  );
}

function UpsellBeacon() {
  const track = useTrack();
  useEffect(() => {
    void track('coaching_upsell_shown' as AppEventName);
  }, [track]);
  return null;
}

export default function CoachingPage() {
  const { t } = useTranslation('coach');
  const { data: entitlement } = useEntitlement();
  const isPremium = entitlement?.isPremium ?? false;

  const { data: expenditure, isLoading: loadingExpenditure } = useCoachingExpenditure();
  const { data: history } = useCoachingHistory();
  const reviewMutation = useCoachingReview();

  const [customKcal, setCustomKcal] = useState('');
  const [reviewResult, setReviewResult] = useState<CoachingReviewResponse | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const isTeaser = expenditure?.teaser ?? !isPremium;
  const currentStatus = reviewResult?.status ?? expenditure?.status ?? 'calibrating';
  const currentEstimate = reviewResult?.estimate ?? expenditure?.estimate ?? null;
  const currentProposal = reviewResult?.proposal ?? null;

  async function runPreview() {
    try {
      const result = await reviewMutation.mutateAsync({ action: 'preview' });
      setReviewResult(result);
    } catch {
      setNotice(t('review.error'));
    }
  }

  async function handleAccept() {
    try {
      const kcal = customKcal ? Number.parseInt(customKcal, 10) : undefined;
      const result = await reviewMutation.mutateAsync({ action: 'accept', customKcal: kcal });
      setReviewResult(result);
      setNotice(
        t('review.accepted', {
          date: result.proposal ? formatDate(result.proposal.effectiveFrom) : '',
        }),
      );
      setCustomKcal('');
    } catch {
      setNotice(t('review.error'));
    }
  }

  async function handleKeep() {
    try {
      await reviewMutation.mutateAsync({ action: 'keep' });
      setNotice(t('review.kept'));
      setReviewResult(null);
    } catch {
      setNotice(t('review.error'));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-medium tracking-tight">{t('review.title')}</h2>
        <p className="mt-1 text-sm leading-6 text-ink-soft">{t('review.subtitle')}</p>
      </div>

      {loadingExpenditure && <p className="text-sm text-ink-soft">{t('review.loading')}</p>}

      {notice && (
        <div
          className="rounded-xl border border-leaf/30 bg-leaf/10 px-4 py-3 text-sm text-ink"
          role="status"
        >
          {notice}
        </div>
      )}

      {/* Free teaser */}
      {!loadingExpenditure && isTeaser && (
        <>
          <UpsellBeacon />
          <div className="rounded-2xl border border-husk bg-paper px-5 py-5">
            <p className="text-sm font-medium text-ink">{t('review.teaser.title')}</p>
            <p className="mt-1 text-sm leading-5 text-ink-soft">{t('review.teaser.body')}</p>
          </div>
          <PremiumGate feature="adaptive_coaching" heading={t('review.teaser.cta')} />
        </>
      )}

      {/* CALIBRATING state */}
      {!loadingExpenditure && !isTeaser && currentStatus === 'calibrating' && (
        <div className="rounded-2xl border border-husk bg-paper px-5 py-5">
          <p className="text-sm font-medium text-ink">{t('review.calibrating.title')}</p>
          <p className="mt-2 text-sm leading-5 text-ink-soft">{t('review.calibrating.body')}</p>
          <p className="mt-3 text-xs text-ink-soft italic">{t('review.calibrating.tip')}</p>
        </div>
      )}

      {/* Expenditure estimate — premium, ready */}
      {!loadingExpenditure &&
        !isTeaser &&
        currentStatus === 'ready' &&
        currentEstimate?.eFinalKcal !== undefined &&
        currentEstimate.confidence !== undefined && (
          <div className="rounded-2xl border border-husk bg-paper px-5 py-5">
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-ink-soft">
              {t('review.expenditure.label')}
            </p>
            <p className="text-3xl font-semibold text-ink">
              {Math.round(currentEstimate.eFinalKcal)} kcal
            </p>
            <p className="mt-2 text-xs text-ink-soft">
              {confidenceLabel(currentEstimate.confidence, t)}
            </p>
          </div>
        )}

      {/* "Why changed" explanation */}
      {!isTeaser && currentProposal && (
        <div className="rounded-2xl border border-husk bg-paper px-5 py-4">
          <p className="text-sm leading-5 text-ink">
            {t(
              currentProposal.explainKey,
              currentProposal.explainParams as Record<string, string | number>,
            )}
          </p>
        </div>
      )}

      {/* Proposal panel */}
      {!isTeaser && currentStatus === 'ready' && currentProposal && (
        <ProposalPanel
          proposal={currentProposal}
          customKcal={customKcal}
          onCustomKcalChange={setCustomKcal}
          onAccept={handleAccept}
          onKeep={handleKeep}
          accepting={reviewMutation.isPending && reviewMutation.variables?.action === 'accept'}
          keeping={reviewMutation.isPending && reviewMutation.variables?.action === 'keep'}
        />
      )}

      {/* Run review button */}
      {!isTeaser && currentStatus !== 'calibrating' && !currentProposal && (
        <button
          type="button"
          onClick={runPreview}
          disabled={reviewMutation.isPending}
          className="self-start rounded-xl bg-leaf px-5 py-2.5 text-sm font-semibold text-white hover:bg-leaf-deep disabled:opacity-60"
        >
          {reviewMutation.isPending ? t('review.running') : t('review.runReview')}
        </button>
      )}

      {/* History */}
      {isPremium && history && history.items.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="text-sm font-medium text-ink hover:text-leaf"
          >
            {showHistory ? '▾' : '▸'} {t('review.history.title')}
          </button>
          {showHistory && (
            <div className="mt-3 flex flex-col gap-3">
              {history.items.map((item) => (
                <div
                  key={item.estimate.id}
                  className="rounded-xl border border-husk bg-paper px-4 py-3"
                >
                  <p className="text-xs text-ink-soft">
                    {t('review.history.windowLabel', {
                      start: formatDate(item.estimate.windowStart),
                      end: formatDate(item.estimate.windowEnd),
                    })}
                  </p>
                  {item.estimate.eFinalKcal !== undefined &&
                    item.estimate.confidence !== undefined && (
                      <p className="mt-1 text-sm text-ink">
                        {Math.round(item.estimate.eFinalKcal)} kcal ·{' '}
                        {t('review.history.confidence', {
                          pct: Math.round(item.estimate.confidence * 100),
                        })}
                      </p>
                    )}
                  <p className="mt-0.5 text-xs text-ink-soft">
                    {item.targetApplied
                      ? t('review.history.targetApplied', { kcal: item.targetApplied.kcal })
                      : t('review.history.targetKept')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cycle insights — phase-aware reassurance + additive nudges (T19.5) */}
      <CycleInsightsPanel />
      <EnergyAvailabilityNotice />
    </div>
  );
}
